const DAILY_RESET_INTERVAL_MS = 24 * 60 * 60 * 1000;
const STABLE_SIGNED_IN_HOME_STATES = new Set([
  'signed_in_default',
  'subscription_active_no_results',
  'daily_available',
]);

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const getUtcTodayIso = (now = new Date()) => now.toISOString().slice(0, 10);

const getNextDailyAvailableAtMillis = (profileData) => {
  const explicitMillis = toMillis(
    profileData?.daily_next_available_at || profileData?.daily_next_available_at_ms
  );
  if (explicitMillis) {
    return explicitMillis;
  }

  const lastCompletedDate = String(profileData?.daily_last_completed_date || '').trim();
  if (!lastCompletedDate) {
    return 0;
  }

  const lastCompletedMidnightUtc = Date.parse(`${lastCompletedDate}T00:00:00Z`);
  if (!Number.isFinite(lastCompletedMidnightUtc)) {
    return 0;
  }

  return lastCompletedMidnightUtc + DAILY_RESET_INTERVAL_MS;
};

const formatNextDailyAvailability = (millis, now = new Date()) => {
  if (!millis) {
    return 'tomorrow';
  }

  const nextDate = new Date(millis);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const startOfDayAfterTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2);

  if (nextDate >= startOfTomorrow && nextDate < startOfDayAfterTomorrow) {
    return `tomorrow at ${nextDate.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })}`;
  }

  if (nextDate >= startOfToday && nextDate < startOfTomorrow) {
    return `today at ${nextDate.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })}`;
  }

  return nextDate.toLocaleString(undefined, {
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const isDailyReadyFromProfile = (profileData, now = new Date()) => {
  const dailyLastCompletedDate = String(profileData?.daily_last_completed_date || '').trim();
  const dailyLastDocId = String(profileData?.daily_last_doc_id || '').trim();
  const latestResultDocId = String(profileData?.latest_result_doc_id || '').trim();

  return (
    dailyLastCompletedDate === getUtcTodayIso(now) &&
    Boolean(dailyLastDocId || latestResultDocId)
  );
};

const isDailyResultFromToday = (documentData, now = new Date()) => (
  Boolean(documentData?.is_daily_generation) &&
  String(documentData?.daily_requested_on || '').trim() === getUtcTodayIso(now)
);

const getFallbackResultHomeState = ({
  subscriptionActive,
  documentData,
  now = new Date(),
}) => {
  if (!subscriptionActive) {
    return 'signed_in_default';
  }

  return isDailyResultFromToday(documentData, now)
    ? 'daily_ready'
    : 'daily_available';
};

const getResolvedHomeStateFromProfile = ({
  userLike,
  profileData = null,
  fallbackHeroState = '',
  subscriptionActive,
  hasCompletedFirstSet,
  now = new Date(),
}) => {
  if (!userLike) {
    return 'guest';
  }

  const dailyGenerationStatus = String(profileData?.daily_generation_status || '').trim().toLowerCase();
  const dailyActiveDocId = String(profileData?.daily_active_doc_id || '').trim();
  const latestResultDocId = String(profileData?.latest_result_doc_id || '').trim();
  const explicitHomeState = String(profileData?.home_state || fallbackHeroState || '').trim();

  if (!subscriptionActive) {
    return 'signed_in_default';
  }
  if (dailyGenerationStatus === 'in_progress' && (dailyActiveDocId || latestResultDocId)) {
    return 'daily_generating';
  }
  if (isDailyReadyFromProfile(profileData, now)) {
    return 'daily_ready';
  }
  if (!hasCompletedFirstSet) {
    return 'subscription_active_no_results';
  }
  if (STABLE_SIGNED_IN_HOME_STATES.has(explicitHomeState)) {
    return explicitHomeState;
  }
  return 'daily_available';
};

export {
  toMillis,
  getUtcTodayIso,
  getNextDailyAvailableAtMillis,
  formatNextDailyAvailability,
  isDailyReadyFromProfile,
  isDailyResultFromToday,
  getFallbackResultHomeState,
  getResolvedHomeStateFromProfile,
};
